import { STREAM_REFETCH_MODE, useUserDeviceEventsStream } from '@/api';
import { useEventStream } from '@/Components/Hooks/useEventStream';
import { roundUpToNextHour } from '@/Components/Sensors/SensorUtils';
import {
  BIOMETRIC_SSE_EVENTS,
  ENGAGEMENT_BUNDLE_SSE_EVENTS,
  SENSOR_SSE_EVENTS,
} from '@/constants/device';
import { getEnvNumberOfDays } from '@/utilities/helpers/commonHelper';
import { useMemo } from 'react';
import { CATEGORIES, getSupportedAlertEventTypesForCategory } from './utils';

// Alert history shown in the Alerts tab and the PDF report (env: ALERTS_HISTORY_NUMBER_OF_DAYS).
export const ALERT_HISTORY_DAYS = getEnvNumberOfDays('ALERTS_HISTORY_NUMBER_OF_DAYS', 90);
// Alerts shown in the Home tab alert cards.
export const ALERT_CARDS_DAYS = 2;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Streams the HC200 alert events (v2, application/x-ndjson), newest first.
 * The window ends at the next full hour, so every component asking for the same `days` within the
 * hour shares one request and its cached result.
 */
export const useAlertEventsStream = ({ useruuid, hc200DeviceUuid, supportedCategories, days }) => {
  const eventTypes = useMemo(
    () => getSupportedAlertEventTypesForCategory(supportedCategories),
    [supportedCategories]
  );
  const endDate = roundUpToNextHour(new Date());
  const startDate = new Date(endDate.getTime() - days * MS_PER_DAY);

  return useUserDeviceEventsStream({
    useruuid,
    deviceuuid: hc200DeviceUuid,
    eventTypes,
    startDate,
    endDate,
    // Alert rows are displayed and ordered by `utc_timestamp`.
    sortBy: 'utc_timestamp',
    // The hour-rounded window keeps the same query key for up to an hour, so a stale query is
    // refetched when another component mounts (e.g. opening the Alerts tab). Keep showing the
    // current rows until the refetch completes instead of clearing and re-streaming them.
    refetchMode: STREAM_REFETCH_MODE.REPLACE,
  });
};

export const useDashboardEventStream = ({ useruuid, supportedCategories, isConfigLoading }) => {
  const supportedAlertEventTypes = useMemo(
    () => getSupportedAlertEventTypesForCategory(supportedCategories),
    [supportedCategories]
  );

  const recentActivitySseEvents = useMemo(
    () => [
      ...(supportedCategories.includes(CATEGORIES.SMART_HOME) ? SENSOR_SSE_EVENTS : []),
      ...(supportedCategories.includes(CATEGORIES.BIOMETRIC) ? BIOMETRIC_SSE_EVENTS : []),
      ...ENGAGEMENT_BUNDLE_SSE_EVENTS,
    ],
    [supportedCategories]
  );

  const { latestEvent, eventStream } = useEventStream(
    { useruuid },
    [...supportedAlertEventTypes, ...recentActivitySseEvents],
    undefined,
    { enabled: !isConfigLoading }
  );

  return { latestEvent, eventStream };
};
